import React from "react";
import { LuCheckSquare, LuSquare } from "react-icons/lu";

import cx from "../ui/cx";
import { pieceLabel, pieceLength } from "../../static/cuttingLine";
import {
  CuttingPieceProps,
  CuttingPlanRowProps,
} from "../../types/cuttingOrder";

const chipClass =
  "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold";

export const owedChipClass = cx(
  chipClass,
  "border-amber-300 bg-amber-50 text-amber-800"
);

const pieceColorClass = (piece: CuttingPieceProps) =>
  piece.bar
    ? "border-[#113F67]/30 bg-white text-[#113F67]"
    : "border-teal-200 bg-teal-50 text-teal-800";

// Pieces as chips: bars in navy, offcuts in teal ("3600mm બાર × 2", "1104mm × 1"), or "કંઈ નથી". hideBarWord leaves
// "બાર" out ("3600mm × 2"). With onToggle each chip is a checkbox: a ticked length is used by the order, an unticked
// one (in skipped) is left alone.
export const CuttingPieces = ({
  pieces,
  sizeUnit,
  hideBarWord,
  skipped,
  onToggle,
}: {
  pieces: CuttingPieceProps[];
  sizeUnit: string;
  hideBarWord?: boolean;
  skipped?: number[];
  onToggle?: (length: number) => void;
}) =>
  pieces.length === 0 ? (
    <span className="text-sm text-gray-500">કંઈ નથી</span>
  ) : (
    <div className="flex flex-wrap gap-1.5">
      {pieces.map((piece) => {
        if (!onToggle) {
          return (
            <span
              key={piece.length}
              className={cx(chipClass, pieceColorClass(piece))}
            >
              {pieceLabel(piece, sizeUnit, !hideBarWord)}
            </span>
          );
        }

        const used = !(skipped || []).includes(piece.length);

        return (
          <button
            key={piece.length}
            type="button"
            role="checkbox"
            aria-checked={used}
            title={
              used
                ? "આ ઓર્ડર માં વપરાશે, અનટીક કરતાં પાર્ટી પાસે રહેશે"
                : "આ ઓર્ડર માં નહીં વપરાય, ટીક કરતાં વપરાશે"
            }
            onClick={() => onToggle(piece.length)}
            className={cx(
              chipClass,
              "gap-1",
              used
                ? pieceColorClass(piece)
                : "border-gray-300 bg-gray-100 text-gray-500"
            )}
          >
            {used ? (
              <LuCheckSquare className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <LuSquare className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span className={cx(!used && "line-through")}>
              {pieceLabel(piece, sizeUnit, !hideBarWord)}
            </span>
          </button>
        );
      })}
    </div>
  );

// One run of identical cuts in lengths: count pieces of `from` each gave qty pieces for `label` ("" for the only line of
// the material, the line's name when there are more, an older order's owed pieces always).
export type CuttingCutView = {
  from: number;
  offcut: boolean;
  count: number;
  qty: number;
  label: string;
};

// What a cut was for: an older order's owed pieces always name the order and the line, a line of this order only
// when the material has more than one.
export const cutLabel = (
  ref: { name: string; orderId: number | null } | null,
  named: boolean
) =>
  !ref
    ? ""
    : ref.orderId !== null
    ? `ઓર્ડર #${ref.orderId} ના ${ref.name} `
    : named
    ? `${ref.name} `
    : "";

// A cut of the API (CuttingStock::eventOut) as CuttingCutRows shows it.
export const cutView = (
  row: CuttingPlanRowProps,
  named: boolean
): CuttingCutView => ({
  from: row.from,
  offcut: row.kind === "offcut",
  count: row.count,
  qty: row.qty,
  label: cutLabel(
    row.ref
      ? { name: row.ref.name, orderId: row.ref.owed ? row.ref.order_id : null }
      : null,
    named
  ),
});

// Whether the cuts are for more than one line of the order itself, so each names its line.
export const namesLines = (rows: CuttingPlanRowProps[]) =>
  new Set(
    rows
      .filter((row) => row.ref && !row.ref.owed)
      .map((row) => row.ref!.line_id)
  ).size > 1;

// The cuts, one per row: "3600mm × 2 → 37 નંગ દરેક", from an offcut "1104mm ટુકડો × 1 → 11 નંગ". From sm up the parts
// line up in columns. What a cut leaves (scrap or an offcut) isn't shown: the user had it removed.
export const CuttingCutRows = ({
  cuts,
  sizeUnit,
}: {
  cuts: CuttingCutView[];
  sizeUnit: string;
}) => (
  <div className="min-w-0 flex-1 space-y-1.5 text-sm sm:grid sm:grid-cols-[auto_auto_minmax(0,1fr)] sm:gap-x-3 sm:gap-y-1 sm:space-y-0">
    {cuts.map((cut, index) => {
      const each = cut.count > 1 ? " દરેક" : "";

      return (
        <div
          key={index}
          className="flex flex-wrap items-baseline gap-x-2 sm:contents"
        >
          <span className="whitespace-nowrap font-semibold text-gray-800">
            {pieceLength(cut.from, sizeUnit)}
            {cut.offcut ? " ટુકડો" : ""} × {cut.count}
          </span>
          <span className="text-gray-400">→</span>
          <span className="font-semibold text-[#113F67]">
            {cut.label}
            {cut.qty} નંગ{each}
          </span>
        </div>
      );
    })}
  </div>
);
